import {useEffect, useRef} from 'react';

import {
  getGoogleIdentityClient,
  setCredentialListener,
} from './googleIdentityClient';

export type CredentialResponse = google.accounts.id.CredentialResponse;

const BUTTON_HEIGHT = {large: 40, medium: 32, small: 20};

type GoogleOauthButtonProps = {
  locale: string;
  onSuccess: (response: CredentialResponse) => void;
  onError?: () => void;
} & Partial<Omit<google.accounts.id.GsiButtonConfiguration, 'locale'>>;

export default function GoogleOauthButton({
  locale,
  onSuccess,
  onError,
  type = 'standard',
  theme = 'outline',
  size = 'large',
  text,
  shape,
  logo_alignment,
  width,
}: GoogleOauthButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    return setCredentialListener((response) => {
      if (!response.credential) {
        onErrorRef.current?.();
        return;
      }
      onSuccessRef.current(response);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const renderGoogleButton = async () => {
      try {
        const accountsId = await getGoogleIdentityClient(locale);
        if (cancelled || !containerRef.current) {
          return;
        }

        accountsId.renderButton(containerRef.current, {
          type,
          theme,
          size,
          text,
          shape,
          logo_alignment,
          width,
          locale,
        });
      } catch {
        if (!cancelled) {
          onErrorRef.current?.();
        }
      }
    };

    renderGoogleButton();

    return () => {
      cancelled = true;
    };
  }, [locale, type, theme, size, text, shape, logo_alignment, width]);

  return <div ref={containerRef} style={{height: BUTTON_HEIGHT[size]}} />;
}
